import React from 'react';
import { Typography } from '@mui/material';
import type { TypographyVariant } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import InlineTextField, {
  type InlineTextFieldProps,
} from '@ui/InlineTextField';

type BuildingNameInputProps = {
  label: string;
  showLabel?: boolean;
  value: string;
  defaultValue?: string;
  variant?: TypographyVariant;
  editing?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  selectOnFocus?: boolean;
  onClick?: InlineTextFieldProps['onClick'];
  onDoubleClick?: InlineTextFieldProps['onDoubleClick'];
  onCancel?: InlineTextFieldProps['onCancel'];
  onSave?: InlineTextFieldProps['onSave'];
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  fullWidth: {
    width: '100%',
    '& > :last-child': {
      width: '100%',
    },
  },
  label: {
    paddingLeft: 4,
    paddingRight: 4,
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.7px',
    lineHeight: 1.56,
    textTransform: 'uppercase',
  },
}));

/** Shared compact building-name control for candidate and persisted buildings. */
export const BuildingNameInput: React.FC<BuildingNameInputProps> = ({
  label,
  showLabel = true,
  value,
  defaultValue,
  variant = 'h4',
  editing = false,
  fullWidth = false,
  autoFocus = true,
  selectOnFocus = false,
  onClick,
  onDoubleClick,
  onCancel,
  onSave,
}) => {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.root, fullWidth && classes.fullWidth)}>
      {showLabel && <Typography className={classes.label}>{label}</Typography>}
      <InlineTextField
        value={value}
        defaultValue={defaultValue}
        variant={variant}
        editing={editing}
        fullWidth={fullWidth}
        autoWidth={!fullWidth}
        autoFocus={autoFocus}
        selectOnFocus={selectOnFocus}
        textFieldProps={{
          InputProps: {
            style: { paddingRight: 4 },
            inputProps: {
              'aria-label': label,
              style: {
                color: 'rgba(0, 0, 0, 0.6)',
                padding: '0 4px',
              },
            },
          },
          sx: {
            '& .MuiOutlinedInput-root': {
              paddingRight: '4px',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              backgroundColor: 'common.white',
            },
            '& .MuiOutlinedInput-input': {
              overflow: 'visible',
              textOverflow: 'clip',
            },
          },
        }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  );
};
